import { useEffect, useState } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { TPlanTask } from '../../models';
import { TCompleteTaskInput } from '../../state/ChangeV2Store';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '420px' },
  muted: { color: tokens.colorNeutralForeground3 },
  block: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
});

type TProps = {
  task?: TPlanTask;
  onClose: () => void;
  onConfirm: (input: TCompleteTaskInput) => void;
};

// The small completion dialog confirms the configured completion criteria and
// captures any deviation.
export const CompleteTaskDialog = ({ task, onClose, onConfirm }: TProps) => {
  const classes = useStyles();
  const [deviations, setDeviations] = useState('');

  useEffect(() => {
    if (task) {
      setDeviations('');
    }
  }, [task]);

  if (!task) {
    return (
      <Dialog open={false}>
        <DialogSurface />
      </Dialog>
    );
  }

  return (
    <Dialog open={Boolean(task)} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={onClose} />
              </DialogTrigger>
            }
          >
            Complete task
          </DialogTitle>
          <DialogContent>
            <div className={classes.body}>
              <Body1>{task.title}</Body1>

              {task.completionCriteria ? (
                <div className={classes.block}>
                  <Caption1 className={classes.muted}>Completion criteria</Caption1>
                  <Body1>{task.completionCriteria}</Body1>
                </div>
              ) : null}

              <Field label="Deviations (optional)">
                <Textarea
                  value={deviations}
                  resize="vertical"
                  onChange={(_, data) => setDeviations(data.value)}
                  placeholder="Any deviations observed while completing this task"
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={() => onConfirm({ deviations: deviations.trim() || undefined })}>
              Mark complete
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
